/**
 * Listing-view tracking — what the client sends (via `sendBeacon`/`fetch
 * keepalive`) to `POST /api/track/listing-view` on mount of the buyer-facing
 * listing detail page. Fire-and-forget: the route ALWAYS responds 204,
 * including on failure — a tracking hiccup must never surface to the buyer.
 *
 * Identity: the wire body carries NO `userId` field. The route resolves the
 * authenticated user id from the session (`supabase.auth.getUser()`) and
 * ALWAYS prefers it over anything client-supplied — see
 * `lib/analytics/resolve-viewer-key.ts` and the route handler, which mirror
 * `lib/cart-perform-add.ts`'s "write identity is resolved from the SESSION,
 * never from client-supplied params" rule.
 *
 * `guestId` IS on the wire — a deliberate, documented deviation from a
 * stricter `{ listingId }`-only reading of the instrumentation spec. This
 * codebase has no server-side guest-identity cookie: `lib/guest.ts` mints
 * and stores the guest id in `localStorage` only (see also
 * `AddToCartRequestSchema.guestId`, `search-history`'s POST body). Without
 * accepting it here, anonymous viewers — most marketplace browsing —
 * could never populate `listing_view_events.viewer_key`'s 'g:<guest_id>'
 * variant, which the raw table's own schema is designed to carry. The field
 * carries no privileged meaning (unlike a claimed `userId`, which this
 * schema deliberately omits): the server uses it ONLY as a fallback when
 * there is no authenticated session, exactly mirroring how the cart
 * resolves identity.
 *
 * @see app/api/track/listing-view/route.ts — handler
 * @see lib/analytics/track-listing-view.ts — canonical client builder
 * @see lib/analytics/resolve-viewer-key.ts — pure viewer_key decision
 */

import { z } from "zod";
import { pinned } from "./pin";
import { TRACKING_CLIENTS, type TrackingClient } from "./tracking-client";

/**
 * Where the view happened. Single SoT for the variant dimension
 * (type-driven-design §6) — the DB CHECK constraint on
 * `listing_view_events.surface`, the builder intent, the tracker prop and
 * the route default all reference these literals.
 *
 * - 'listing_page' — buyer-facing /listings/[id] page.
 * - 'chat_sheet'   — ChatListingDetailSheet (chat message tap / feed
 *   quick-checkout detail step).
 */
export const LISTING_VIEW_SURFACES = ["listing_page", "chat_sheet"] as const;
export type ListingViewSurface = (typeof LISTING_VIEW_SURFACES)[number];

export interface TrackListingViewRequest {
  listingId: string;
  /** Guest identity fallback — see file header. Ignored when a session is present. */
  guestId?: string | null;
  /**
   * Where the view happened. OPTIONAL on the wire — the SERVER defaults it
   * to 'listing_page' when omitted, so the pre-surface beacon shape stays
   * valid (backward-compatible).
   */
  surface?: ListingViewSurface;
  /**
   * Which client produced the view. OPTIONAL — the SERVER defaults omitted
   * -> 'web' (web builders keep omitting it); mobile always sends 'mobile'.
   * See tracking-client.ts header.
   */
  client?: TrackingClient;
}

export const TrackListingViewRequestSchema = pinned<TrackListingViewRequest>()(
  z.object({
    listingId: z.string().min(1),
    guestId: z.string().min(1).nullable().optional(),
    surface: z.enum(LISTING_VIEW_SURFACES).optional(),
    client: z.enum(TRACKING_CLIENTS).optional(),
  }),
);
